import { useEffect, useMemo, useState } from 'react';
import { globalBus } from '../streams/wsBus';
import { useActionFeedback } from '../streams/useActionFeedback';

type Message = { from: string; role?: string; text: string };

export function ChatPanel() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const { states, fire } = useActionFeedback(globalBus);

  useEffect(() => {
    const unsub = globalBus.subscribe((message) => {
      if (message.t === '__status') {
        setStatus(message.status);
      }
      if (message.t === 'scene' && message.id === 'hivecore.chat') {
        const list = message.body?.widgets?.find((w: any) => w.key === 'chat.messages')?.items ?? [];
        setMessages(list);
      }
    });
    globalBus.sendIntent('scene.request', { id: 'hivecore.chat' });
    return unsub;
  }, []);

  const connectionChip = useMemo(() => {
    switch (status) {
      case 'connected': return { label: 'Connected', tone: 'ok' };
      case 'disconnected': return { label: 'Reconnecting…', tone: 'warn' };
      default: return { label: 'Connecting…', tone: 'warn' };
    }
  }, [status]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    if (text.startsWith('/')) {
      fire('chat.palette.btn', 'palette.invoke', { cmd: text.slice(1) });
    } else {
      fire('chat.send.btn', 'chat.send', { text });
    }
    setInput('');
  };

  const sendState = states['chat.send.btn'] ?? 'idle';

  return (
    <div className="panel chat-panel">
      <header className="panel-header">
        <h2>Unified Chat</h2>
        <span className={`chip ${connectionChip.tone}`}>{connectionChip.label}</span>
      </header>
      <div className="chat-body" role="log" aria-live="polite">
        {messages.length === 0 ? (
          <div className="empty-state">No messages yet. Type /help for commands.</div>
        ) : (
          messages.map((msg, idx) => (
            <div key={`${msg.from}-${idx}`} className={`chat-msg role-${msg.role ?? 'user'}`}>
              <strong>{msg.from}</strong>
              <p>{msg.text}</p>
            </div>
          ))
        )}
      </div>
      <div className="chat-input" role="form">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Message HiveCore or type /command"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button data-state={sendState} onClick={handleSend}>Send</button>
      </div>
    </div>
  );
}
